import React, { useState, useEffect, useRef } from 'react';
import Router from 'next/router';
// import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import md5 from 'md5';
import { throttle } from 'lodash-es';
import { serverSideTranslation } from '../../util/next';
import Page from '../../components/Layout/Page';
import NewsContent from '../../modules/NewsContent';
import NewsDatailBanner, { type DataType } from '../../modules/NewsDatailBanner';
import BackToTop from '../../components/BackToTop';
import { getNewsFormalDetail, getNewsPreviewDetail, getNewsPage, NewsCenterItem } from '../../api/newsApi';
import { genShareConfig } from '../../components/Share/use-config-share';
import NewsRelated from '../../modules/NewsRelated';
import NewsShare from '../../modules/NewsShare';

interface Props {
  newsNo: string;
  newsTitle: string;
  newsType: string;
  newsTypeId: number;
  newsDate: string;
  newsOrigin: string;
  newsContent: string;
  newsCenterData: NewsCenterItem[];
  newsAbstract: string;
  thumbnailPic: string;
  /* 新闻分享缩略图 */
  sharePic: string;
  _nextI18Next: any;
}

const NewsDetail = (prop: Props) => {
  const {
    newsNo,
    newsTitle,
    newsType,
    newsTypeId,
    newsDate,
    newsOrigin,
    newsContent,
    newsAbstract,
    _nextI18Next: i18n,
  } = prop;
  const bannerBg = 'https://p0.meituan.net/smartvenus/b57807e9a752bf76b1d72714d409aa0f43379.png';
  const { t } = useTranslation('common');
  const newsTextData: DataType = t('newsTextData', { returnObjects: true });
  const { qrCodeText, copyUrlSuccessText, copyUrlFailedText } = t('newsShareConfig', { returnObjects: true });
  const bannerStyle = {
    backgroundImage: `url(${bannerBg})`,
  };

  const [backVisible, setBackVisible] = useState(false);
  const scrollPositionRef = useRef(0);

  const isBrowser = () => typeof window !== 'undefined';
  const handleScroll = () => {
    const position = window.pageYOffset;
    scrollPositionRef.current = position;
  };

  useEffect(() => {
    if (!newsContent) {
      Router.push({
        pathname: `/news`,
      });
    }
  }, [newsContent]);

  useEffect(() => {
    if (!isBrowser()) return () => {};
    if (isBrowser()) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!scrollPositionRef.current) {
      return;
    }
    const func = throttle(() => {
      if (scrollPositionRef.current >= 400) {
        setBackVisible(true);
      } else {
        setBackVisible(false);
      }
    }, 500);
    func();
  }, [scrollPositionRef]);

  return (
    <Page description={newsAbstract} title={newsTitle}>
      <div className="news-datail-box bg-white lg:pb-28">
        <div className="w-full bg-cover bg-center bg-no-repeat" style={bannerStyle}>
          <NewsDatailBanner
            newsTitle={newsTitle}
            newsTypeId={newsTypeId}
            newsTextData={newsTextData}
            newsType={newsType}
            newsDate={newsDate}
            newsOrigin={newsOrigin}
          />
        </div>

        <div className="relative flex flex-col p-5 md:m-auto md:max-w-[892px] md:flex-row md:py-5 lg:px-0">
          <NewsContent newsContent={newsContent} />
          <div className="absolute hidden lg:left-full sl:block">
            <NewsShare
              newsNo={newsNo}
              qrCodeText={qrCodeText}
              copyUrlSuccessText={copyUrlSuccessText}
              copyUrlFailedText={copyUrlFailedText}
            />
          </div>
        </div>
        <div className="lg:hidden">
          <BackToTop
            visible={backVisible}
            back={() => {
              window.scrollTo({ behavior: 'smooth', top: 0, left: 0 });
            }}
          />
        </div>
      </div>
      <NewsRelated newsNo={newsNo} category={newsTypeId} lanType={i18n.initialLocale} />
    </Page>
  );
};

// 服务端渲染
export async function getServerSideProps({ locale, query, res }: any) {
  const newsNo = query.id;
  const newsSource = query.source || '';
  const params = {
    lanType: locale,
    newsNo,
  };
  let getNewsRequest;
  const { preview, previewTime, auth } = query;
  // 需要校验通过才允许开启预览
  const cryptoKey = '7f3a1d9b8c2e4a5p0q9';
  // 三分钟之后过期
  const isInTime = Date.now() - previewTime <= 1000 * 60 * 3;
  const authPass = auth === md5(`${previewTime}${cryptoKey}`);
  if (preview && previewTime && auth && isInTime && authPass) {
    getNewsRequest = getNewsPreviewDetail;
  } else {
    getNewsRequest = getNewsFormalDetail;
  }
  try {
    const data: any = await getNewsRequest(params);
    const newsPageParams = {
      pageSize: 3,
      pageNo: 1,
      newsClassifyId: data.newsTypeId,
      lanType: locale,
      notNewsNo: newsNo,
    };

    const { pageContent: newsCenterData } = await getNewsPage(newsPageParams);

    res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

    const { newsTitle, newsAbstract, sharePic } = data;

    return {
      props: {
        lxCustom: {
          cid: 'c_donation_yd4hb6os',
          locale,
          id: newsNo,
          newsSource,
          ...data,
          newsContent: false,
        },
        newsNo,
        newsCenterData,
        ...genShareConfig({
          shareContent: {
            shareTitle: newsTitle,
            shareDetail: newsAbstract,
            shareImg: sharePic ? `${sharePic}@100w` : sharePic,
          },
        }),
        ...(await serverSideTranslation(locale, ['cookie', 'common'])),
        ...data,
      },
    };
  } catch (error) {
    return {
      props: {
        lxCustom: {
          cid: 'c_donation_yd4hb6os',
          locale,
          id: newsNo,
          newsSource,
          newsContent: false,
        },
        ...(await serverSideTranslation(locale, ['cookie', 'common'])),
      },
      // 错误的处理直接展示错误信息
      redirect: {
        destination: '/500',
        permanent: false,
      },
    };
  }
}

export default NewsDetail;
